import { c as _c } from "react/compiler-runtime";
import type { ReactNode } from "react";
import ReactMarkdown from "react-markdown";
import { ProgressBarLink } from "@/components/progress-bar";
import { cx } from "@/lib/tailwind-utils";
interface ChatMarkdownProps {
  content: string;
}

/** True if markdown emitted an empty paragraph (extra newlines from the model). */
function isEffectivelyEmptyMarkdownNode(node: ReactNode): boolean {
  if (node == null || node === false) return true;
  if (typeof node === "string") return node.trim() === "";
  if (Array.isArray(node)) return node.length === 0 || node.every(isEffectivelyEmptyMarkdownNode);
  return false;
}
const FENCED_CODE_BLOCK = /(```[\s\S]*?```)/g;

/** An unfilled Next.js route placeholder, e.g. the `[id]` in `/settings/phone-numbers/[id]`. */
const UNRESOLVED_ROUTE_PARAM = /\/\[[^/\]]+\]/;

/**
 * Collapse runaway blank lines in prose while leaving fenced code blocks untouched.
 */
function normalizeChatMarkdownSource(text: string): string {
  if (!text) return text;
  const segments = text.split(FENCED_CODE_BLOCK);
  return segments.map((segment, index) => {
    if (index % 2 === 1) return segment;
    return segment.replace(/\r\n/g, "\n").replace(/[ \t]+\n/g, "\n").replace(/\n{3,}/g, "\n\n");
  }).join("").trim();
}
export function ChatMarkdown(t0) {
  const $ = _c(5);
  const {
    content
  } = t0;
  let t1;
  if ($[0] !== content) {
    t1 = normalizeChatMarkdownSource(content);
    $[0] = content;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const normalized = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      p: _temp,
      h2: _temp2,
      h3: _temp3,
      ul: _temp4,
      ol: _temp5,
      li: _temp6,
      strong: _temp7,
      pre: _temp8,
      code: _temp9,
      a: _temp10
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== normalized) {
    t3 = <ReactMarkdown components={t2 as any}>{normalized}</ReactMarkdown>;
    $[3] = normalized;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
}
function _temp10(t0) {
  const {
    href,
    children: children_8
  } = t0;
  if (!href) {
    return <span>{children_8}</span>;
  }
  let normalizedHref = href.replace(/\.mdx$/, "");
  const localhostMatch = normalizedHref.match(/^https?:\/\/localhost:\d+\/(.+)/);
  if (localhostMatch) {
    normalizedHref = `https://docs.decodahealth.com/${localhostMatch[1]}`;
  }
  if (normalizedHref.startsWith("/")) {
    if (UNRESOLVED_ROUTE_PARAM.test(normalizedHref)) {
      return <span>{children_8}</span>;
    }
    return <ProgressBarLink href={normalizedHref} className="underline decoration-current/70 hover:decoration-current">{children_8}</ProgressBarLink>;
  }
  return <a href={normalizedHref} target="_blank" rel="noopener noreferrer" className="underline decoration-current/70 hover:decoration-current">{children_8}</a>;
}
function _temp9(t0) {
  const {
    children: children_7,
    className
  } = t0;
  return <code className={cx(className, "rounded bg-primary-foreground/15 px-1 py-0.5 text-caption", "[pre_&]:rounded-none [pre_&]:bg-transparent [pre_&]:p-0 [pre_&]:text-inherit")}>{children_7}</code>;
}
function _temp8(t0) {
  const {
    children: children_6
  } = t0;
  return <pre className="my-2 max-w-full overflow-x-auto rounded-lg bg-primary-foreground/15 p-3 text-caption leading-relaxed last:mb-0 [&>code]:block [&>code]:min-w-max [&>code]:whitespace-pre">{children_6}</pre>;
}
function _temp7(t0) {
  const {
    children: children_5
  } = t0;
  return <strong className="font-semibold">{children_5}</strong>;
}
function _temp6(t0) {
  const {
    children: children_4
  } = t0;
  return <li className="leading-relaxed ml-4">{children_4}</li>;
}
function _temp5(t0) {
  const {
    children: children_3
  } = t0;
  return <ol className="mb-1 list-decimal space-y-0.5 pl-4 text-body text-foreground-secondary last:mb-0">{children_3}</ol>;
}
function _temp4(t0) {
  const {
    children: children_2
  } = t0;
  return <ul className="mb-1 list-disc space-y-0.5 pl-4 text-body text-foreground-secondary last:mb-0">{children_2}</ul>;
}
function _temp3(t0) {
  const {
    children: children_1
  } = t0;
  return <h3 className="mb-0.5 mt-1.5 text-body-strong first:mt-0">{children_1}</h3>;
}
function _temp2(t0) {
  const {
    children: children_0
  } = t0;
  return <h2 className="mb-0.5 mt-2 text-body-emphasis first:mt-0">{children_0}</h2>;
}
function _temp(t0) {
  const {
    children
  } = t0;
  return isEffectivelyEmptyMarkdownNode(children) ? null : <p className="mb-1 text-body !leading-relaxed last:mb-0">{children}</p>;
}